import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
export interface CartItem {
  id: string;
  category: string;
  product: string;
  quantity: number;
  pricePerYard: number;
  // How this item entered the cart — used for order-source analytics in the
  // confirmation email. Defaults to 'cart' for all standard product page /
  // materials hub additions. 'estimator' indicates the Instant Estimator flow.
  source?: 'estimator' | 'cart';
}
export interface PromoState {
  code: string;
  discount: number; // dollar amount off materials subtotal
  error: string;
}
interface CartContextType {
  items: CartItem[];
  isCartOpen: boolean;
  addToCart: (item: Omit<CartItem, 'id'>) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  loadSavedCart: (savedItems: CartItem[]) => void;
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  totalItems: number;
  totalPrice: number;
  totalYards: number;
  deliveryCount: number;
  deliveryAddress: string;
  deliveryFee: number;
  totalDeliveryFee: number;
  deliveryZone: number | null;
  isCalculatingDelivery: boolean;
  setDeliveryInfo: (address: string, fee: number, zone: number | null) => void;
  setIsCalculatingDelivery: (isCalculating: boolean) => void;
  clearDeliveryInfo: () => void;
  promo: PromoState;
  applyPromoCode: (code: string) => boolean;
  clearPromoCode: () => void;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
const STORAGE_KEY_ITEMS = '253gravel_cart_items';
const STORAGE_KEY_DELIVERY = '253gravel_cart_delivery';
const STORAGE_KEY_PROMO = '253gravel_cart_promo';
function loadFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>(() => loadFromStorage<CartItem[]>(STORAGE_KEY_ITEMS, []));
  const [isCartOpen, setIsCartOpen] = useState(false);
  const initialDelivery = loadFromStorage<{
    address: string;
    fee: number;
    zone: number | null;
  }>(STORAGE_KEY_DELIVERY, {
    address: '',
    fee: 0,
    zone: null
  });
  const [deliveryAddress, setDeliveryAddress] = useState(initialDelivery.address);
  const [deliveryFee, setDeliveryFee] = useState(initialDelivery.fee);
  const [deliveryZone, setDeliveryZone] = useState<number | null>(initialDelivery.zone);
  const [isCalculatingDelivery, setIsCalculatingDeliveryState] = useState(false);
  const [promoCodeState, setPromoCodeState] = useState<string>(() => loadFromStorage<string>(STORAGE_KEY_PROMO, ''));
  const [promoError, setPromoError] = useState('');
  // Persist items
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY_ITEMS, JSON.stringify(items));
    } catch {}
  }, [items]);
  // Persist delivery info
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY_DELIVERY, JSON.stringify({
        address: deliveryAddress,
        fee: deliveryFee,
        zone: deliveryZone
      }));
    } catch {}
  }, [deliveryAddress, deliveryFee, deliveryZone]);
  // Persist promo code
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY_PROMO, JSON.stringify(promoCodeState));
    } catch {}
  }, [promoCodeState]);
  const setDeliveryInfo = useCallback((address: string, fee: number, zone: number | null) => {
    setDeliveryAddress(address);
    setDeliveryFee(fee);
    setDeliveryZone(zone);
  }, []);
  const clearDeliveryInfo = useCallback(() => {
    setDeliveryAddress('');
    setDeliveryFee(0);
    setDeliveryZone(null);
  }, []);
  const setIsCalculatingDelivery = useCallback((isCalculating: boolean) => {
    setIsCalculatingDeliveryState(isCalculating);
  }, []);
  const addToCart = (newItem: Omit<CartItem, 'id'>) => {
    setItems(prevItems => {
      // Generate a unique ID for each cart entry so same materials aren't merged
      const id = `${newItem.category}-${newItem.product}-${Date.now()}`;
      return [...prevItems, {
        ...newItem,
        id
      }];
    });
  };
  const removeFromCart = (id: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
  };
  const updateQuantity = (id: string, quantity: number) => {
    const item = items.find(i => i.id === id);
    const n = item?.product.toLowerCase() || '';
    // 50/50 Mix is classified HEAVY (12 yd full load) — not included in
    // the light list even though the name reads "mix" with topsoil.
    const isLight = n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips');
    const minQty = 1;
    const maxQty = isLight ? 60 : 48;
    if (quantity < minQty || quantity > maxQty) {
      return; // Min/max yards enforcement
    }
    setItems(prevItems => prevItems.map(item => item.id === id ? {
      ...item,
      quantity
    } : item));
  };
  const clearCart = () => {
    setItems([]);
    clearDeliveryInfo();
  };
  const loadSavedCart = (savedItems: CartItem[]) => {
    setItems(savedItems);
  };
  const toggleCart = () => setIsCartOpen(prev => !prev);
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const totalItems = items.length;
  const totalPrice = (() => {
    // Aggregate total yards per product for bulk discount calculation
    const productYards: Record<string, number> = {};
    const baseProductName = (name: string) => name.replace(/\s*\(Delivery \d+\)/, '');
    for (const item of items) {
      const base = baseProductName(item.product);
      productYards[base] = (productYards[base] || 0) + item.quantity;
    }
    return items.reduce((sum, item) => {
      const n = item.product.toLowerCase();
      // 50/50 Mix is classified HEAVY (12 yd full load) — heavier blend
      // (sand + topsoil) ships in the same truckload as rock/aggregates.
      const isLight = n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips');
      const threshold = 10;
      const billableYards = item.quantity;
      const surcharge = (() => {
        if (billableYards < 3) return 1; // Flat $250 delivery fee covers economics
        if (billableYards >= threshold) return 1;
        return 1 + (threshold - billableYards) * 0.1;
      })();
      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
      let itemTotal = billableYards * effectivePrice;
      // Tiered bulk discount: light materials 5% at 15yds, others 5% at 24yds; 10% at 4 full loads
      const aggYards = productYards[baseProductName(item.product)] || item.quantity;
      const fullLoadYards = isLight ? 15 : 12;
      const bulkPct = aggYards >= fullLoadYards * 4 ? 0.1 : aggYards >= (isLight ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      if (bulkPct > 0) {
        itemTotal = Math.round(itemTotal * (1 - bulkPct));
      }
      return sum + itemTotal;
    }, 0);
  })();
  // Promo code logic.
  //   FIRST10 = 10% off materials subtotal, first-order only (public).
  //   FAM20   = 20% off materials subtotal. PRIVATE — never surfaced or
  //             hinted anywhere in the UI. Single-use per device: once an
  //             order is placed with it, the '253gravel_fam20_used' flag is
  //             set (see CartDrawer) and the code can't be applied again.
  //             Only one promo can be active at a time, so it never stacks.
  const safeGet = (key: string): string | null => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  };
  const isFirstOrder = !safeGet('253gravel_last_order');
  const fam20Used = !!safeGet('253gravel_fam20_used');
  const promoDiscount = (() => {
    if (promoCodeState === 'FIRST10' && isFirstOrder) {
      return Math.round(totalPrice * 0.1 * 100) / 100;
    }
    if (promoCodeState === 'FAM20' && !fam20Used) {
      return Math.round(totalPrice * 0.2 * 100) / 100;
    }
    return 0;
  })();
  const applyPromoCode = useCallback((code: string) => {
    const normalized = code.trim().toUpperCase();
    if (!normalized) {
      setPromoError('Enter a code');
      return false;
    }
    if (normalized === 'FIRST10') {
      if (!isFirstOrder) {
        setPromoError('FIRST10 is for first-time orders only');
        setPromoCodeState('');
        return false;
      }
      setPromoCodeState('FIRST10');
      setPromoError('');
      return true;
    }
    if (normalized === 'FAM20') {
      // Private single-use family code. Reject if already redeemed on
      // this device. Applying it replaces any other active promo, so it
      // never combines with another code.
      if (fam20Used) {
        setPromoError('This code has already been used');
        setPromoCodeState('');
        return false;
      }
      setPromoCodeState('FAM20');
      setPromoError('');
      return true;
    }
    setPromoError('Invalid promo code');
    setPromoCodeState('');
    return false;
  }, [isFirstOrder, fam20Used]);
  const clearPromoCode = useCallback(() => {
    setPromoCodeState('');
    setPromoError('');
  }, []);
  const totalYards = items.reduce((sum, item) => sum + item.quantity, 0);
  const lightYards = items.reduce((sum, item) => {
    const n = item.product.toLowerCase();
    // 50/50 Mix is HEAVY — counts toward `otherYards`, not `lightYards`.
    const isLight = n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips');
    return isLight ? sum + item.quantity : sum;
  }, 0);
  const otherYards = totalYards - lightYards;
  const deliveryCount = (lightYards > 0 ? Math.ceil(lightYards / 15) : 0) + (otherYards > 0 ? Math.ceil(otherYards / 12) : 0) || 1;
  const totalDeliveryFee = items.reduce((sum, item) => {
    if (item.quantity <= 0) return sum;
    // Micro-loads (< 3 yd) get a flat $250 delivery fee — one truck run.
    if (item.quantity < 3) return sum + 250;
    // Otherwise the item is billed the standard zone fee PER TRUCKLOAD.
    // A single line item can exceed one truck's capacity (e.g. 24 yd of
    // 5/8" Minus needs two 12 yd loads), so we charge the zone fee for
    // each load rather than once per cart line. This keeps the fee in
    // sync with `deliveryCount` (which also counts truckloads).
    const n = item.product.toLowerCase();
    const isLight = n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips');
    const fullLoad = isLight ? 15 : 12;
    const loads = Math.ceil(item.quantity / fullLoad);
    return sum + loads * deliveryFee;
  }, 0);
  return <CartContext.Provider value={{
    items,
    isCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    loadSavedCart,
    toggleCart,
    openCart,
    closeCart,
    totalItems,
    totalPrice,
    totalYards,
    deliveryCount,
    deliveryAddress,
    deliveryFee,
    totalDeliveryFee,
    deliveryZone,
    isCalculatingDelivery,
    setDeliveryInfo,
    setIsCalculatingDelivery,
    clearDeliveryInfo,
    promo: {
      code: promoCodeState,
      discount: promoDiscount,
      error: promoError
    },
    applyPromoCode,
    clearPromoCode
  }} data-id="element-228" data-mp="27cuyqdhk04gf">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}